import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { StandupCardItem } from '../types';
import { Filters } from './useStandUpCardsFilters';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/standUpSlice';

export interface UseStandUpCardsDataParams {
  filters: Filters;
  selectedLocation?: { id?: number; displayText?: string };
  userRoleId?: number | null;
}

export interface UseStandUpCardsDataReturn {
  isLoading: boolean;
  fetchFilteredData: () => Promise<StandupCardItem[]>;
}

export const useStandUpCardsData = ({
  filters,
  selectedLocation,
  userRoleId,
}: UseStandUpCardsDataParams): UseStandUpCardsDataReturn => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch filtered data from API
  const fetchFilteredData = useCallback(async (): Promise<StandupCardItem[]> => {
    try {
      setIsLoading(true);
      const api = createApiClient();
      
      // Get client timestamp and timezone
      const clientTimestamp = getLocalTimestamp();
      const clientTimezone = getTimezoneOffsetString();
      // Build query parameters
      const params = new URLSearchParams();
      params.append('location_id', selectedLocation?.id?.toString() || '1053');
      if (userRoleId) {
        params.append('roleID', userRoleId.toString());
      }
      params.append('location_name', selectedLocation?.displayText || 'Hayward CA');
      params.append('clientTimestamp', clientTimestamp);
      params.append('clientTimezone', clientTimezone);
      
      if (filters.status) params.append('status', filters.status);
      if (filters.standupType) params.append('standup_type', filters.standupType);
      if (filters.createdBy) params.append('created_by', filters.createdBy);
      if (filters.modifiedBy) params.append('modified_by', filters.modifiedBy);
      if (filters.date) {
        // Convert date from MM/DD/YYYY to MM-DD-YYYY format
        const dateValue = filters.date.replace(/\//g, '-');
        params.append('date', dateValue);
      }

      const response = await apiClient(
        api,
        `/stand-up?${params.toString()}`,
        'GET'
      );

      if (response.success && response.data) {
        // Map API response to StandupCardItem format
        const mappedItems = response.data.map((item: any) => ({
          id: item.id?.toString() || '',
          title: item.title || '',
          status: item.status || '',
          statusColor: item.status === 'Completed' ? '#027C06' : 
                       item.status === 'In Progress' ? '#0662C4' : 
                       item.status === 'Saved' ? '#D6AC16' : 
                       item.status === 'Incomplete' ? '#c12c01' : 
                       item.status === 'Not Started' ? '#CDCDCD' : '#CDCDCD',
          modified_at: item.modified_at || '',
          modified_by: item.modified_by || '',
          createdBy: item.created_by || '',
          date: item.scheduled_date || '',
          type: item.standup_type || '',
          STANDUP_ID: item.id,
          STANDUP_TYPE: item.standup_type,
          LOCATION_ID: item.location_id,
          LOCATION_NAME: item.location_name,
          DATE_RANGE: item.scheduled_date,
          CREATED_BY: item.created_by,
          MODIFIED_BY: item.modified_by,
          CREATED_AT: item.created_at,
          MODIFIED_AT: item.modified_at,
          STATUS: item.status,
        }));
        return mappedItems;
      }
      return [];
    } catch (error: any) {
      console.error('Error fetching filtered data:', error);
      
      // Don't show toast for 404 errors (no data available)
      if (error.response?.status !== 404) {
        let errorMessage = 'Failed to load standup data';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        dispatch(
          setToastNotification({
            header: "Error",
            subtext: errorMessage,
            variant: "error",
            position: "top",
            duration: 6000,
          }),
        );
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [filters, selectedLocation, userRoleId, dispatch]);

  return {
    isLoading,
    fetchFilteredData,
  };
};
